"use strict";

import angular from "angular";
import { Company } from "../../../gateway/Company/Company";
import { DashboardAvailable } from "../../../gateway/DashboardAvailable/DashboardAvailable";
import { GroupOfCompaniesGateway } from "../../../gateway/GroupOfCompanies/GroupOfCompaniesGateway";
import { Point } from "../../../gateway/Point/Point";
import { PointCalculated } from "../../../gateway/PointCalculated/PointCalculated";
import { Serie } from "../../../gateway/Serie/Serie";
import { User } from "../../../gateway/User/User";
import { UserDataAdapter } from "../../../presentation/adpters/UserDataAdapter";
import { AccountingContractManager } from "../../../presentation/services/AccountingContractManager/AccountingContractManager";
import { PopulateManager } from "../../../presentation/services/PopulateManager/PopulateManager";
import { UpdateUserBrowserStorageUsecase } from "../../../usecases";
import { AvailablePopulateUsecase } from "../../../usecases/available-populate.usecase";
import { SaveManyUserUsecase } from "../../../usecases/save-many-user.usecase";
import {
    customerUsers,
    nucontersUsers
} from "../../../utils/users-roles/users-roles";

angular.module("nucont.accounting.group").config(["$stateProvider", routeConfig]);

function routeConfig($stateProvider) {
    $stateProvider
        .state("nucont.accounting.group", {
            url: "/grupo/:id",
            lazyLoad(transition) {
                const $ocLazyLoad = transition.injector().get("$ocLazyLoad");
                return import(
                    /* webpackChunkName: "feature-accounting-group" */ "./group.entry"
                ).then(() => $ocLazyLoad.inject("nucont.accounting.group"));
            },
            views: {
                "admin@nucont.accounting": {
                    component: "groupComponent"
                }
            },
            data: {
                rolesGuard: [...nucontersUsers, ...customerUsers],
                layoutConfig: {
                    model: "LayoutConfigModel5",
                    ignore: { object: "headerConfig", path: "title" }
                }
            },
            resolve: {
                groupData: [
                    "$rootScope",
                    "$stateParams",
                    "NavigationService",
                    "LayoutService",
                    async (
                        $rootScope,
                        $stateParams,
                        NavigationService,
                        LayoutService
                    ) => {
                        const gateway = new GroupOfCompaniesGateway();
                        let group = await gateway.findById($stateParams.id);
                        group.companies = await new Company().getByIds(
                            group.companyIds
                        );
                        group.isGroup = true;
                        LayoutService.setAttrs([
                            {
                                object: "headerConfig",
                                path: "title.text",
                                value: group.name
                            },
                            {
                                object: "headerConfig",
                                path: "title.enabled",
                                value: true
                            }
                        ]);

                        $rootScope.company = group;

                        setTimeout(() => {
                            NavigationService.setParams("id", $stateParams.id);
                            NavigationService.setParams(
                                "accountingId",
                                $stateParams.accountingId
                            );
                            NavigationService.setHref();
                        }, 1000);

                        return group;
                    }
                ],

                AvailablePopulate: [
                    "$stateParams",
                    "groupData",
                    async ($stateParams, groupData) => {
                        new Point().deleteMany({});
                        new PointCalculated().deleteMany({});
                        new PopulateManager().deleteMany({});
                        new Serie().deleteMany({});

                        const availablePopulateUsecase = new AvailablePopulateUsecase();
                        const dashboardAvailable = new DashboardAvailable();

                        const groupId = $stateParams.id;

                        await availablePopulateUsecase.execute({
                            companyId: groupData.companyIds,
                            groupId,
                            groupStartDate: groupData.dateFrom
                        });

                        await dashboardAvailable.populate({
                            companyId: groupId
                        });
                    }
                ],
                setTheme: [
                    "ThemesService",
                    "accountingData",
                    "accountingPlanEntity",
                    async (
                        ThemesService,
                        accountingData,
                        accountingPlanEntity
                    ) => {
                        const user = new UserDataAdapter().user();
                        const UserDomain = new User();

                        const hasPermission = new AccountingContractManager({
                            User: UserDomain
                        }).hasPermission({
                            contract: accountingData.contract,
                            user: user,
                            feature: "dashboardThemes",
                            accountingPlanEntity: accountingPlanEntity
                        });

                        if (
                            hasPermission === "expired" &&
                            user.profile.dashboardTheme !== "default"
                        ) {
                            const saveManyUserUsecase = new SaveManyUserUsecase();
                            const userId = new UserDataAdapter().user()._id;
                            saveManyUserUsecase.execute({
                                accountingId: accountingData._id,
                                users: [
                                    { _id: userId, dashboardTheme: "default" }
                                ]
                            });
                            const updateUserBrowserStorageUsecase = new UpdateUserBrowserStorageUsecase();
                            updateUserBrowserStorageUsecase.execute({
                                dashboardTheme: "default"
                            });
                            ThemesService.setCurrentDashboardTheme("default");
                        } else {
                            ThemesService.setCurrentDashboardTheme(
                                user.profile.dashboardTheme || "default"
                            );
                        }
                    }
                ]
            }
        })
        .state("nucont.accounting.group.dashboards", {
            url: "/dashboard/:dashboardId",
            params: {
                dashboardId: { value: null, dynamic: true }
            }
        })
        .state("nucont.accounting.group.monitoring", {
            url:
                "/monitoramento/:source/:date/:scenario?filter&accountPlanType",
            lazyLoad(transition) {
                const $ocLazyLoad = transition.injector().get("$ocLazyLoad");
                return import(
                    /* webpackChunkName: "feature-accounting-company-monitoring" */ "../company/monitoring/monitoring.entry"
                ).then(() =>
                    Promise.all([
                        $ocLazyLoad.inject("nucont.accounting.company.monitoring"),
                        $ocLazyLoad.inject("nucont.accounting.company.showtime")
                    ])
                );
            },
            params: {
                source: { value: null, dynamic: true },
                date: { value: null, dynamic: true },
                scenario: { value: null, dynamic: true },
                filter: { value: null, dynamic: true },
                accountPlanType: { value: null, dynamic: true }
            },
            views: {
                "admin@nucont.accounting": {
                    component: "monitoringCompanyComponent",
                    bindings: { companyData: "groupData" }
                }
            }
        })
        .state("nucont.accounting.group.demonstrations", {
            lazyLoad(transition) {
                const $ocLazyLoad = transition.injector().get("$ocLazyLoad");
                return import(
                    /* webpackChunkName: "feature-accounting-company-demonstrations" */ "../company/demonstrations/demonstrations.entry"
                ).then(() =>
                    Promise.all([
                        $ocLazyLoad.inject("nucont.accounting.company.demonstrations"),
                        $ocLazyLoad.inject("nucont.accounting.company.showtime")
                    ])
                );
            },
            views: {
                "admin@nucont.accounting": {
                    component: "demonstrationsComponent",
                    bindings: { companyData: "groupData" }
                }
            }
        })
        .state("nucont.accounting.group.accountsPlan", {
            url: "/plano-de-contas/:source/:year?type",
            lazyLoad(transition) {
                const $ocLazyLoad = transition.injector().get("$ocLazyLoad");
                return import(
                    /* webpackChunkName: "feature-accounting-group" */ "./group.entry"
                ).then(() => $ocLazyLoad.inject("nucont.accounting.group"));
            },
            params: {
                source: { value: null, dynamic: true },
                year: { value: null, dynamic: true },
                type: { value: null, dynamic: true }
            },
            views: {
                "admin@nucont.accounting": {
                    component: "groupAccountsPlanComponent"
                }
            }
        })
        .state("nucont.accounting.group.customization", {
            url: "/customizacao/:source?",
            lazyLoad(transition) {
                const $ocLazyLoad = transition.injector().get("$ocLazyLoad");
                return import(
                    /* webpackChunkName: "feature-accounting-company-customization" */ "../company/customization/customization.entry"
                ).then(() =>
                    $ocLazyLoad.inject("nucont.accounting.company.customization")
                );
            },
            views: {
                "admin@nucont.accounting": {
                    component: "customizationComponent",
                    bindings: { companyData: "groupData" }
                }
            },
            params: {
                source: {
                    value: null,
                    dynamic: true
                }
            }
        });
}
